import { Box, Card, CardActionArea, CardContent, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

interface AssignmentCountSectionProps {
  count: number | null | undefined;
  color: string;
  title: string;
  showDivider?: boolean;
}
const AssignmentCountSection = ({
  count,
  color,
  title,
  showDivider = true,
}: AssignmentCountSectionProps) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'end' }}>
      {count != null ? (
        <Typography variant="h4" color={color} sx={{ fontWeight: '500' }}>
          {`${count.toLocaleString()}`}
        </Typography>
      ) : (
        <Typography variant="h4" sx={{ fontWeight: '500' }}>
          --
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: '500' }}>
        {title}
      </Typography>
      {showDivider && (
        <Typography variant="h4" sx={{ fontWeight: '500' }}>
          |
        </Typography>
      )}
    </Box>
  );
};

interface AssignmentCardProps {
  title: string;
  description?: string;
  onClickHandler: (e: React.MouseEvent) => void;
  subtitle?: string;
  pendingCount?: number | null;
  draftCount?: number | null;
  completedCount?: number | null;
  borderColor?: string;
}

export const AssignmentCard = ({
  title,
  onClickHandler,
  subtitle,
  pendingCount,
  draftCount,
  completedCount,
  borderColor,
  description = '',
}: AssignmentCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Card
      sx={{
        width: '315px',
        height: '150px',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardActionArea
        onClick={(e) => onClickHandler(e)}
        sx={{
          height: '100%',
          '&[data-active]': {
            backgroundColor: 'action.selected',
            '&:hover': {
              backgroundColor: 'action.selectedHover',
            },
          },
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            borderLeft: (theme) => `3px solid ${borderColor ?? theme.palette.primary.main}`,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: '500' }}>
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              minHeight: theme.spacing(6),
            }}
          >
            {subtitle ?? ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'end' }}>
            <Typography variant="h4" sx={{ fontWeight: '500' }}>
              {pendingCount?.toLocaleString() ?? '--'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: '500' }}>
              {formatMessage({
                id: 'annotationDashboard.annotation.currentAssignments.card.pending',
              })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'end' }}>
            <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'end' }}>
              <Typography variant="subtitle1">{draftCount?.toLocaleString() ?? '--'}</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {formatMessage({
                  id: 'annotationDashboard.annotation.currentAssignments.card.draft',
                })}
              </Typography>
            </Box>
            <Typography variant="subtitle1">•</Typography>
            <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'end' }}>
              <Typography variant="subtitle1">
                {completedCount?.toLocaleString() ?? '--'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {formatMessage({
                  id: 'annotationDashboard.annotation.currentAssignments.card.finalized',
                })}
              </Typography>
            </Box>
          </Box>
          <Typography variant="subtitle1" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
